import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Stack, 
  IconButton, 
  Grid, 
  Button, 
  useMediaQuery, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  Tooltip, 
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Delete, MoreVert, FilterAlt } from '@mui/icons-material';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { departmentSnapshotListener, deleteDepartment, updateDepartmentManagers } from '../../../../repository/DepartmentsDAO';
import ConfirmDeleteModal from '../../../../components/modals/ConfirmDeleteModal';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { Link as RouterLink } from 'react-router-dom';
import { handleMenuClick, handleMenuClose } from '../../../../components/utils/MenuHandlers';
import FilterDialog from '../../../../components/filter_dialog/FilterDialog';

const DepartmentDetail = () => {

  const { departmentId } = useParams();
  const { organization, getEmployeeByID } = useOrganizationContext();
  const { firebaseUser } = useUserContext();
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [department, setDepartment] = useState();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedManagers, setSelectedManagers] = useState({});
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [managerFilter, setManagerFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    if (department && department.employees) {
      const existingManagers = Object.keys(department.employees)
        .filter(employeeId => department.employees[employeeId].is_manager);
      
      const initialSelectedManagers = existingManagers.reduce((acc, managerId) => {
        acc[managerId] = true;
        return acc;
      }, {});

      setSelectedManagers(initialSelectedManagers);
    }
  }, [department]);

  useEffect(() => {
    if (firebaseUser) {
      departmentSnapshotListener(firebaseUser.organization, departmentId, setDepartment);
    }
  }, [firebaseUser, departmentId]);

  const handleManagerSelection = async (employeeId) => {
    try {
      if(!hasPermission(Modules.DEPARTMENTS, PermissionTypes.WRITE)) return;
      setLoadingData(true);
      const isManager = !selectedManagers[employeeId];
      const result = await updateDepartmentManagers(
        organization.id, 
        departmentId, 
        employeeId, 
        isManager
      );
      
      if (result.success) {
        setSelectedManagers(prev => ({
          ...prev,
          [employeeId]: isManager
        }));
        showToast(t(StringKeys.SUCCESS),ToastType.SUCCESS);
      } else {
        showToast(t(StringKeys.SOMETHING_WENT_WRONG), ToastType.DANGER);
      }
    } catch (error) {
      console.error('Error updating manager status:', error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG), ToastType.DANGER);
    } finally {
      setLoadingData(false);
    }
  };

  const handleDelete = async () => {
    setLoadingData(true);

    try {
      const result = await deleteDepartment(organization.id, departmentId);

      if (result.success) {
        showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, { x: `${department.name}` }), ToastType.SUCCESS);
        navigate('/departments', { replace: true });
      } else {
        showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: `${department.name}` }), ToastType.DANGER);
      }
    } catch (error) {
      showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: `${department.name}` }), ToastType.DANGER);
    } finally {
      setLoadingData(false);
    }
  };

  const handleOpenBottomSheet = () => {
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
  };

  const getFilteredEmployees = () => {
    if (!department || !department.employees) return [];
    
    return Object.keys(department.employees)
      .filter(employeeId => {
        const employee = getEmployeeByID(employeeId);
        if (!employee) return false;
        
        // Manager filter
        const isManager = !!selectedManagers[employeeId];
        if (managerFilter === 'managers' && !isManager) return false;
        if (managerFilter === 'non-managers' && isManager) return false;
        
        // Active filter
        if (activeFilter === 'active' && !employee.active) return false;
        if (activeFilter === 'inactive' && employee.active) return false;
        
        // Search term
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchLower);
        const emailMatch = employee.email && employee.email.toLowerCase().includes(searchLower);
        const phoneMatch = employee.phone_number && employee.phone_number.includes(searchTerm);
        
        return searchTerm === '' || nameMatch || emailMatch || phoneMatch;
      })
      .sort((a, b) => {
        const employeeA = getEmployeeByID(a);
        const employeeB = getEmployeeByID(b);
        if (!employeeA) return 1;
        if (!employeeB) return -1;
        
        // Apply sort order
        if (sortOrder === 'asc') {
          return employeeA.last_name.localeCompare(employeeB.last_name) || 
                 employeeA.first_name.localeCompare(employeeB.first_name);
        } else {
          return employeeB.last_name.localeCompare(employeeA.last_name) || 
                 employeeB.first_name.localeCompare(employeeA.first_name);
        }
      });
  };

  if (!department) return null;
  const deleteButton = (
    <Tooltip
      title={Object.keys(department.employees || {}).length > 0 ? t(StringKeys.CANNOT_DELETE_A_DEPARTMENT_THAT_CURRENTLY_HAS_EMPLOYEES_ASSIGNED_TO_IT) : ""}
      disableHoverListener={Object.keys(department.employees || {}).length === 0}
    >
      <span>
        <Button
          variant="text"
          startIcon={<Delete />}
          onClick={() => setConfirmOpen(true)}
          disabled={Object.keys(department.employees || {}).length > 0}
        >
          {t(StringKeys.DELETE)}
        </Button>
      </span>
    </Tooltip>
  );

  const filteredEmployees = getFilteredEmployees();

  return (
    <AppSidebar
      content={
        <Stack gap={4}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <Typography variant="h5">{department.name}</Typography>
            </Grid>
            {!isMobile && hasPermission(Modules.DEPARTMENTS, PermissionTypes.WRITE) && (
              <><Grid item>{deleteButton}</Grid></>              
            )}
            {isMobile && hasPermission(Modules.DEPARTMENTS, PermissionTypes.WRITE) && (
              <Grid item xs="auto">
                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => handleMenuClose(setAnchorEl)}
                >
                  <MenuItem onClick={() => { handleOpenBottomSheet(); handleMenuClose(setAnchorEl); }}>
                    <ListItemIcon>
                      <FilterAlt />
                    </ListItemIcon>
                    {t(StringKeys.FILTER)}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setConfirmOpen(true);
                      handleMenuClose(setAnchorEl);
                    }}
                    disabled={Object.keys(department.employees || {}).length > 0}
                  >
                    <ListItemIcon>
                      <Delete fontSize="small" />
                    </ListItemIcon>
                    {t(StringKeys.DELETE)}
                  </MenuItem>                
                </Menu>
              </Grid>
            )}
          </Grid>

          {/* Search and Filter Controls */}
          <Grid container spacing={2} alignItems="center">
            {!isMobile && (
              <>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label={t(StringKeys.SEARCH)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t(StringKeys.SEARCH_BY_NAME_EMAIL_PHONE)}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>{t(StringKeys.MANAGER)}</InputLabel>
                    <Select
                      value={managerFilter}
                      onChange={(e) => setManagerFilter(e.target.value)}
                      label={t(StringKeys.MANAGER)}
                    >
                      <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                      <MenuItem value="managers">{t(StringKeys.MANAGERS)}</MenuItem>
                      <MenuItem value="non-managers">{t(StringKeys.NON_MANAGERS)}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                    <Select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      label={t(StringKeys.STATUS)}
                    >
                      <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                      <MenuItem value="active">{t(StringKeys.ACTIVE)}</MenuItem>
                      <MenuItem value="inactive">{t(StringKeys.INACTIVE)}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>{t(StringKeys.SORT)}</InputLabel>
                    <Select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      label={t(StringKeys.SORT)}
                    >
                      <MenuItem value="asc">A-Z</MenuItem>
                      <MenuItem value="desc">Z-A</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {isMobile && (
              <Grid item xs={12}>
                <TextField
                  label={t(StringKeys.SEARCH)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t(StringKeys.SEARCH_BY_NAME_EMAIL_PHONE)}
                />
              </Grid>
            )}
          </Grid>

          <TableContainer 
            sx={{ 
              overflowX: 'auto',
              width: '100%',
              '& .MuiTable-root': {
                minWidth: 650, // Ensures table maintains minimum width on mobile
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t(StringKeys.MANAGER)}</TableCell>
                  <TableCell>{t(StringKeys.NAME)}</TableCell>
                  <TableCell>{t(StringKeys.EMAIL)}</TableCell>
                  <TableCell>{t(StringKeys.PHONE)}</TableCell>
                  <TableCell>{t(StringKeys.STATUS)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employeeId) => {
                    const employee = getEmployeeByID(employeeId);
                    const isManager = !!selectedManagers[employeeId];
                    return employee ? (
                      <TableRow key={employeeId}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isManager}
                            onChange={() => handleManagerSelection(employeeId)}
                            color={isManager ? "primary" : "default"}
                            disabled={!hasPermission(Modules.DEPARTMENTS, PermissionTypes.WRITE)}
                          />
                        </TableCell>
                        <TableCell>
                          <Link component={RouterLink} to={`/employees/${employeeId}`}>
                            {employee.first_name} {employee.last_name}
                          </Link>
                        </TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>{employee.phone_number}</TableCell>
                        <TableCell>{employee.active ? t(StringKeys.ACTIVE) : t(StringKeys.INACTIVE)}</TableCell>
                      </TableRow>
                    ) : null;
                  }).filter(Boolean)
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t(StringKeys.NO_EMPLOYEES_ASSIGNED)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <ConfirmDeleteModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => {
              handleDelete();
              setConfirmOpen(false);
            }}
            item={department.name}
          />

          {/* Filter Dialog for Mobile */}
          <FilterDialog
            open={isBottomSheetOpen}
            onClose={handleCloseBottomSheet}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            filters={[
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{t(StringKeys.MANAGER)}</InputLabel>
                  <Select
                    value={managerFilter}
                    onChange={(e) => setManagerFilter(e.target.value)}
                    label={t(StringKeys.MANAGER)}
                  >
                    <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                    <MenuItem value="managers">{t(StringKeys.MANAGERS)}</MenuItem>
                    <MenuItem value="non-managers">{t(StringKeys.NON_MANAGERS)}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>,
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                  <Select
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                    label={t(StringKeys.STATUS)}
                  >
                    <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                    <MenuItem value="active">{t(StringKeys.ACTIVE)}</MenuItem>
                    <MenuItem value="inactive">{t(StringKeys.INACTIVE)}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            ]}
          />
        </Stack>
      }
    />
  );
};

export default DepartmentDetail;
